import attach from 'assets/image/icons/svg/attach.svg'
import attachments from 'assets/image/icons/svg/icons/attchments.svg'
import clsx from 'clsx'
import { ResponsiveButton } from 'components/atoms/responsiveButton'
import {
	type ChangeEventHandler,
	type FormEventHandler,
	useEffect,
	useRef,
	useState
} from 'react'
import { useActionCreators, useAppSelector } from 'shared/hooks'
import { StackIcon } from 'shared/ui'

import { api } from '../../api'
import { useCurrentId } from '../../hooks'
import { actions } from '../../model/slice'
import { selectors } from '../../utils/selectors'
import { FormEmojiPicker } from '../Emoji/FormPicker'
import css from './MessageForm.module.scss'
import { useRead } from './useRead'
import { insertCharacterAtCursor } from './utils/addChar'

interface InnerForm extends HTMLFormElement {
	text: HTMLTextAreaElement
}

export function MessageForm({
	isExpand,
	toggleExpand
}: {
	isExpand: boolean
	toggleExpand: () => void
}) {
	const conversation = useCurrentId()

	const [disableSend, setDisableSend] = useState(true)

	const [sendMessage, sendMessageProps] = api.useSendMessageMutation()
	const [updateMessage, updateMessageProps] = api.useEditMessageMutation()

	const { resetEditMessage } = useActionCreators(actions)
	const messageToEdit = useAppSelector(selectors.editMessage)

	const isNewMessage = messageToEdit === null
	const isEditMode = !isNewMessage

	const checkIfChanged: ChangeEventHandler<HTMLTextAreaElement> = event => {
		const area = event.currentTarget

		const isInitialValue = isEditMode && area.value === messageToEdit.text

		setDisableSend(isInitialValue || area.value.trim().length === 0)
	}

	const ref = useRef<InnerForm | null>(null)

	const cancelEdit: FormEventHandler<HTMLFormElement> = () => {
		resetEditMessage()
		setDisableSend(true)
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: as planned
	useEffect(() => ref.current?.reset(), [conversation])

	useEffect(() => {
		if (messageToEdit?.text && ref.current) {
			const area = ref.current.text
			area.value = messageToEdit.text
			area?.focus()
		}
	}, [messageToEdit?.text])

	const markRead = useRead()

	const handleSubmit: FormEventHandler<InnerForm> = event => {
		event.preventDefault()

		const form = event.currentTarget,
			body = {
				conversation,
				message: form.text.value
			}

		const mutation = isEditMode
			? updateMessage({ ...body, id: messageToEdit.id })
			: sendMessage(body)

		mutation
			.unwrap()
			.catch(console.error)
			.then(() => form.reset())
	}

	return (
		<form
			className={clsx(
				css.form,
				isEditMode && css.editMode,
				isExpand && css.expanded
			)}
			onReset={isEditMode ? cancelEdit : undefined}
			onSubmit={handleSubmit}
			ref={ref}
		>
			{isEditMode && <p className={css.edit}>Edit Message</p>}

			<textarea
				className={css.area}
				disabled={sendMessageProps.isLoading || updateMessageProps.isLoading}
				name='text'
				onChange={checkIfChanged}
				onFocus={markRead}
				placeholder='Write a message...'
			/>

			<button
				aria-expanded={isExpand}
				aria-label='Expand textarea'
				className={`${css.expand} li-⚪`}
				onClick={toggleExpand}
				type='button'
			>
				<StackIcon icon='arrow' width={16} />
			</button>

			<div className={css.actions}>
				<div className={css.subActions}>
					{isNewMessage && [
						<img alt='attachments' key='⛓️‍💥' src={attachments} />,
						<img alt='attach' key='🗃️' src={attach} />
					]}

					<FormEmojiPicker
						onEmojiClick={emoji => {
							const area = ref.current?.text

							if (area) {
								insertCharacterAtCursor(area, emoji)
							}
						}}
					/>
				</div>
				{isNewMessage ? (
					<ResponsiveButton
						className={css.button}
						disabled={disableSend}
						isLoading={sendMessageProps.isLoading}
						type='submit'
					>
						Send
					</ResponsiveButton>
				) : (
					<div className={css.submits}>
						<ResponsiveButton className={css.button} type='reset'>
							Cancel
						</ResponsiveButton>
						<ResponsiveButton
							className={css.button}
							disabled={disableSend}
							isLoading={updateMessageProps.isLoading}
							type='submit'
						>
							Save
						</ResponsiveButton>
					</div>
				)}
			</div>
		</form>
	)
}
